'use client';

import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';

type PriceDeltaProps = {
  /** The price value in cents */
  value: number;
  /** The currency code (e.g., 'EUR') */
  currencyCode: string;
  /** Whether the amount is negative (determines color and sign) */
  isNegative: boolean;
  /** Optional size for the Price component */
  size?: 'default' | 'small';
  /** Optional additional className */
  className?: string;
};

/**
 * A price component that displays a value with a + or - prefix
 * and appropriate color styling based on whether it's positive or negative.
 *
 * Negative values are shown in red (text-color-sale)
 * Positive values are shown in green (text-color-success-dark) with a + prefix
 */
export const PriceDelta = ({
  value,
  currencyCode,
  isNegative,
  size = 'small',
  className,
}: PriceDeltaProps) => {
  const { formatPrice } = useFormatPrice();

  const colorClass = isNegative ? 'text-color-sale' : 'text-color-success-dark';
  const prefix = isNegative ? '' : '+';

  return (
    <Price size={size} className={className ?? colorClass}>
      <PriceValue>
        {prefix}
        {formatPrice(value, currencyCode)}
      </PriceValue>
    </Price>
  );
};
